import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable, of } from 'rxjs';
import {
  catchError,
  concatMap,
  map,
  mergeMap,
  take,
  tap,
} from 'rxjs/operators';
import { isNullish, NotificationService } from 'wflow-cdk';
import { IntegrationsAPIService } from 'wflow-main-app/src/app/core/api';
import { FieldValidationsAPIService } from 'wflow-main-app/src/app/core/api/api/customization/field-validations.service';
import { ERPIntegrators } from 'wflow-main-app/src/app/core/api/model/erp-integrator';
import {
  AccountingFields,
  KindFieldValidations,
  ValidationSettingsField,
} from 'wflow-main-app/src/app/core/api/model/field-validations';
import { AccountingFieldsAggregate } from 'wflow-main-app/src/app/domains/accounting-validations/aggregates/accounting-fields.aggregate';
import { ValidationAggregate } from 'wflow-main-app/src/app/domains/accounting-validations/aggregates/validation.aggregate';
import { buildDefaultAccountingFields } from 'wflow-main-app/src/app/pages/org/pages/admin/pages/customisation-page/subpages/erp-fields-customisation/service/erp-fields-customisation.utils';
import { getOrg } from 'wflow-main-app/src/app/shared/utils/route.utils';
import { KindEnum } from 'wflow-variables';
import { getDisabledFieldsForErp } from '../../shared/__const/erp-list.const';
import { OrganizationsStore } from '../organizations/organizations.model';
import { selectCurrentOrganizationSettings } from '../settings/settings.selectors';
import {
  ChangeIntegrationERP,
  LoadIntegration,
  LoadIntegrationItemsSuccess,
  LoadIntegrationSettings,
  SetAccountingItems,
  SetFailureIntegration,
  SetIntegrationSuccess,
  SetIntegrator,
  SetSuccessIntegration,
  SettingIntegration,
  SetValidationItems,
} from './integration.actions';
import {
  AccountingFieldItem,
  CustomIntegration,
  KindCustomizeValidationSettings,
} from './integration.model';
import {
  IntegrationStore,
  selectIntegrationAccountingFields,
  selectIntegrationKind,
  selectIntegrationKinds,
} from './integration.selector';
import { IntegrationUtils } from './utils/integration.utils';

const setSuccess = () => ({
  orgId: getOrg(),
});

const setError = (error: Error) => ({
  orgId: getOrg(),
  data: error,
});

@Injectable()
export class IntegrationEffects {
  private _getIntegration$ = (org: string) =>
    this.integrationsSvc.get(org) as Observable<CustomIntegration>;
  private _getCustomizeValidationSettings$ = (org: string) =>
    this.fieldValidationsService.getFieldAndValidations(org);

  private _defaultErpId = 'other';
  private _noneErpId = 'none';

  readonly loadIntegration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadIntegration),
      mergeMap((payload: { orgId: string }) =>
        this._getIntegration$(payload?.orgId).pipe(
          map((data) =>
            SetIntegrationSuccess({
              orgId: payload?.orgId ?? getOrg(),
              data,
            }),
          ),
          tap(() => this.store.dispatch(SetSuccessIntegration(setSuccess()))),
          catchError((error) => of(SetFailureIntegration(setError(error)))),
        ),
      ),
    ),
  );

  readonly saveAccounting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SetAccountingItems),
      concatMap(
        (payload: {
          orgId: string;
          kind?: KindEnum;
          items: AccountingFieldItem;
        }) => {
          const accountingFields = AccountingFieldsAggregate.create(
            payload.items,
          );
          return this._setIntegrationKind(
            payload.kind,
            accountingFields,
            'accounting',
          );
        },
      ),
      mergeMap((fields) =>
        this.fieldValidationsService
          .setFieldAndValidations(getOrg(), fields)
          .pipe(
            tap(() => {
              this.store.dispatch(
                LoadIntegrationItemsSuccess({
                  orgId: getOrg(),
                  data: fields,
                }),
              );
            }),
            map(() => {
              this.notificationSvc.success({
                text: this.translate.instant('Ng_PbSaved'),
                title: this.translate.instant(
                  'AdminOrg_Customization_Accounting_Title',
                ),
              });
              return SetSuccessIntegration(setSuccess());
            }),
            catchError((error) => of(SetFailureIntegration(setError(error)))),
          ),
      ),
    ),
  );

  readonly saveValidation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SetValidationItems),
      concatMap((payload: { kind: KindEnum; items: ValidationSettingsField }) =>
        this._setIntegrationKind(
          payload.kind,
          ValidationAggregate.create(payload.items),
          'validation',
        ),
      ),
      mergeMap((fields) =>
        this.fieldValidationsService
          .setFieldAndValidations(getOrg(), fields)
          .pipe(
            tap(() =>
              this.store.dispatch(
                LoadIntegrationItemsSuccess({
                  orgId: getOrg(),
                  data: fields,
                }),
              ),
            ),
            map(() => {
              this.notificationSvc.success({
                text: this.translate.instant('Ng_PbSaved'),
                title: this.translate.instant(
                  'AdminOrg_Customization_Accounting_ValidationSettings',
                ),
              });
              return SetSuccessIntegration(setSuccess());
            }),
            catchError((error) => of(SetFailureIntegration(setError(error)))),
          ),
      ),
    ),
  );

  readonly loadIntegrationSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadIntegrationSettings),
      mergeMap(() =>
        combineLatest([
          this._getIntegration$(getOrg()),
          this._getCustomizeValidationSettings$(getOrg()),
          this.store
            .select(selectCurrentOrganizationSettings)
            .pipe(map((orgSettings) => orgSettings?.vatCountry)),
        ]).pipe(
          tap(([integration, settings]) => {
            const orgId = getOrg();

            this._updateOldAccountingData(settings, integration?.erp?.id);

            this.store.dispatch(
              SetIntegrationSuccess({
                orgId,
                data: integration,
              }),
            );
            this.store.dispatch(
              LoadIntegrationItemsSuccess({
                orgId: getOrg(),
                data: settings,
              }),
            );
          }),
          map(([integration, settings, vatCountry]) => {
            const erp = integration?.erp?.id || this._defaultErpId;
            const updatedSettings =
              IntegrationUtils.getUpdatedIntegrationSettingsByKind(settings);

            return {
              erp,
              items: {
                ...updatedSettings,
                accounting: IntegrationUtils.accountingMandatoryOff(
                  updatedSettings.accounting,
                ),
              },
              vatCountry,
            };
          }),
          map(() => SetSuccessIntegration(setSuccess())),
          catchError((error) => of(SetFailureIntegration(setError(error)))),
        ),
      ),
    ),
  );

  readonly changeErp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChangeIntegrationERP),
      map((payload: { id }) => payload?.id || this._defaultErpId),
      mergeMap((_erp) =>
        combineLatest([
          of(_erp),
          this.store.select(selectIntegrationAccountingFields),
          this.store
            .select(selectCurrentOrganizationSettings)
            .pipe(map((orgSettings) => orgSettings?.vatCountry)),
        ]).pipe(
          take(1),
          map(([erp, items, vatCountry]) => ({
            erp,
            items,
            vatCountry,
          })),
          map(() => SetSuccessIntegration(setSuccess())),
        ),
      ),
    ),
  );

  readonly putIntegration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingIntegration),
      mergeMap((payload: { id: string; currentKind?: KindEnum }) =>
        this._getIntegration$(getOrg()).pipe(
          take(1),
          map((integration) => {
            const data: CustomIntegration = {
              ...(integration || {}),
              erp: {
                ...integration?.erp,
                id: payload?.id,
              },
            };
            return { integration: data, currentKind: payload?.currentKind };
          }),
        ),
      ),
      mergeMap(({ integration, currentKind }) =>
        this._setIntegrationWithItems(integration, currentKind),
      ),
    ),
  );

  readonly putIntegrator$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SetIntegrator),
      mergeMap((payload: { erp?: string; client?: string }) =>
        this._getIntegration$(getOrg()).pipe(
          take(1),
          map((integration) => {
            const data: CustomIntegration = {
              ...(integration || {}),
              erp: {
                ...(integration?.erp || {}),
                ...(payload?.client === undefined
                  ? {}
                  : { integrator: payload.client as ERPIntegrators }),
                ...(payload?.erp === undefined
                  ? {}
                  : {
                      id: payload.erp === null ? this._noneErpId : payload.erp,
                    }),
              },
            };
            return data;
          }),
        ),
      ),
      mergeMap(this._setIntegrationWithItems.bind(this)),
    ),
  );

  constructor(
    private actions$: Actions,
    private fieldValidationsService: FieldValidationsAPIService,
    private notificationSvc: NotificationService,
    private translate: TranslateService,
    private store: Store<IntegrationStore & OrganizationsStore>,
    private integrationsSvc: IntegrationsAPIService,
  ) {}

  private _setIntegrationWithItems(
    _integration: CustomIntegration,
    currentKind?: KindEnum,
  ) {
    return combineLatest([
      this.store.select(selectIntegrationAccountingFields),
      this.store.select(selectIntegrationKinds),
      this.store
        .select(selectCurrentOrganizationSettings)
        .pipe(map((orgSettings) => orgSettings?.vatCountry)),
      this.integrationsSvc.save(_integration),
    ]).pipe(
      take(1),
      mergeMap(([fields, kinds, vatCountry]) => {
        const updatedKinds = this._buildKindsWithErpDefaults(
          fields,
          kinds,
          _integration.erp.id,
          vatCountry,
          currentKind,
        );
        return this._saveKindFieldValidations(updatedKinds);
      }),
    );
  }

  private _saveKindFieldValidations(
    updatedKinds: KindCustomizeValidationSettings,
  ) {
    return this.fieldValidationsService
      .setFieldAndValidations(getOrg(), updatedKinds as KindFieldValidations)
      .pipe(
        tap(() => {
          this.store.dispatch(
            LoadIntegrationItemsSuccess({
              orgId: getOrg(),
              data: updatedKinds,
            }),
          );
          this.store.dispatch(LoadIntegrationSettings());
        }),
        map(() => {
          this.notificationSvc.success({
            text: this.translate.instant('Ng_PbSaved'),
            title: this.translate.instant('Integrations'),
          });
          return SetSuccessIntegration(setSuccess());
        }),
        catchError((error) => of(SetFailureIntegration(setError(error)))),
      );
  }

  private _buildKindsWithErpDefaults(
    fields: AccountingFieldItem,
    kinds: KindCustomizeValidationSettings,
    erpCode: string,
    vatCountry: string,
    currentKind?: KindEnum,
  ): KindCustomizeValidationSettings {
    const result: KindCustomizeValidationSettings = {};
    Object.keys(kinds ?? {}).forEach((kindKey) => {
      const perKindFields =
        kindKey === currentKind
          ? fields
          : buildDefaultAccountingFields(
              Object.keys(fields ?? {}),
              erpCode,
              vatCountry,
              kindKey as KindEnum,
            );
      result[kindKey] = {
        ...kinds[kindKey],
        accounting: AccountingFieldsAggregate.create(perKindFields),
      };
    });
    return result;
  }

  private _setIntegrationKind(
    kindName: KindEnum,
    items: AccountingFields | ValidationSettingsField,
    type: 'accounting' | 'validation',
  ): Observable<KindCustomizeValidationSettings> {
    return combineLatest([
      this.store.select(
        selectIntegrationKind(kindName || KindEnum.IncomingInvoice),
      ),
      this.store.select(selectIntegrationKinds),
    ]).pipe(
      take(1),
      map(([kind, kinds]) => {
        let data = {};
        /** TODO: Remove when adding kind separation for accounting */
        if (isNullish(kindName)) {
          Object.keys(kinds).forEach((key) => {
            data[key] = {
              ...kinds[key],
              [type]: items,
            };
          });
        } else {
          data = {
            ...kinds,
            [kindName]: {
              ...kind,
              [type]: items,
            },
          };
        }

        return data as KindCustomizeValidationSettings;
      }),
    );
  }

  private _updateOldAccountingData(
    settings: KindFieldValidations,
    erpCode: string,
  ): void {
    if (!!settings && erpCode) {
      const disabledFieldSettings = getDisabledFieldsForErp(erpCode);

      disabledFieldSettings.forEach((field, key) => {
        Object.values(settings).forEach((kind) => {
          if (kind.accounting?.[key]) {
            kind.accounting[key].linesVisible = !field.linesDisabled;
          }
        });
      });
    }
  }
}
